import React, { useState } from 'react';
import type { VideoProject, FlowPackage } from './videoStudioTypes';
import { videoStudioApi } from './videoStudioApi';

interface VideoReviewPanelProps {
  project: VideoProject;
  onRefresh: () => void;
  onNavigateToVersions?: () => void;
  onBeforeExport?: () => Promise<any>;
}

export const VideoReviewPanel: React.FC<VideoReviewPanelProps> = ({ project, onNavigateToVersions, onBeforeExport }) => {
  const [flowPkg, setFlowPkg] = useState<FlowPackage | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState(false);

  const totalDuration = (project.scenes || []).reduce((acc, s) => acc + (Number(s.duration_seconds) || 0), 0);
  const hasReferences = (project.references || []).length > 0;
  const hasScenes = (project.scenes || []).length > 0;
  const isDurationValid = totalDuration === (project.target_duration_seconds || 15);
  const hasScript = Boolean(project.script_hook && project.script_cta);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      if (onBeforeExport) {
        await onBeforeExport();
      }
      const res = await videoStudioApi.exportFlowPackage(project.campaign_id, project.creative_id);
      if (res.ok && res.package) {
        setFlowPkg(res.package);
      }
    } catch (err) {
      console.error('Erro ao exportar pacote do Flow:', err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleCopyJson = () => {
    if (!flowPkg) return;
    navigator.clipboard.writeText(JSON.stringify(flowPkg, null, 2));
    setCopyFeedback(true);
    setTimeout(() => setCopyFeedback(false), 2500);
  };

  const handleDownloadJson = () => {
    if (!flowPkg) return;
    const blob = new Blob([JSON.stringify(flowPkg, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `flow-package-${project.creative_id}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div style={{ marginBottom: '1.25rem' }}>
        <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Revisão & Pacote de Exportação Flow</h4>
        <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.825rem', color: 'var(--text-secondary, #64748b)' }}>
          Valide a consistência do projeto e gere o pacote de especificações técnicas para execução no Flow.
        </p>
      </div>

      <div className="vstudio-card" style={{ marginBottom: '1.5rem' }}>
        <h5 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', fontWeight: 600 }}>Checklist de Prontidão</h5>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
            <span>{hasReferences ? '✅' : '⚠️'}</span>
            <span>Referências ({project.references?.length || 0})</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
            <span>{hasScenes ? '✅' : '❌'}</span>
            <span>Cenas Definidas ({project.scenes?.length || 0})</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
            <span>{isDurationValid ? '✅' : '⚠️'}</span>
            <span>Timing Total ({totalDuration}s / {project.target_duration_seconds}s)</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
            <span>{hasScript ? '✅' : '⚠️'}</span>
            <span>Roteiro & CTA definidos</span>
          </div>
        </div>
      </div>

      <div className="vstudio-card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <div>
            <h5 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600 }}>Exportação Técnica para o Flow</h5>
            <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.8rem', color: 'var(--text-secondary, #64748b)' }}>
              Consolida prompts, referências e tempos em um único pacote estruturado.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {onNavigateToVersions && (
              <button
                type="button"
                className="vstudio-btn"
                style={{
                  background: 'linear-gradient(135deg, #2563eb, #7c3aed)',
                  color: '#fff',
                  border: 'none',
                  fontWeight: 700
                }}
                onClick={onNavigateToVersions}
              >
                🚀 Gerar com Google Veo
              </button>
            )}
            <button
              type="button"
              className="vstudio-btn vstudio-btn-primary"
              onClick={handleExport}
              disabled={isExporting}
            >
              {isExporting ? 'Compilando...' : '📦 Gerar Pacote Flow'}
            </button>
          </div>
        </div>

        {flowPkg && (
          <div>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <button
                type="button"
                className="vstudio-btn vstudio-btn-secondary"
                onClick={handleCopyJson}
              >
                {copyFeedback ? '✓ Copiado com Sucesso!' : '📋 Copiar JSON'}
              </button>
              <button
                type="button"
                className="vstudio-btn vstudio-btn-outline"
                onClick={handleDownloadJson}
              >
                💾 Baixar Pacote (.json)
              </button>
            </div>

            <pre className="vstudio-code-block">
              {JSON.stringify(flowPkg, null, 2)}
            </pre>
          </div>
        )}
      </div>

      <div className="vstudio-card">
        <h5 style={{ margin: '0 0 0.5rem 0', fontSize: '0.9rem', fontWeight: 600 }}>Próximos Passos do Operador (Flow Pilot)</h5>
        <ol style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.825rem', color: 'var(--text-secondary, #64748b)', lineHeight: '1.6' }}>
          <li>Exporte ou copie o JSON com os prompts e referências deste projeto.</li>
          <li>Abra o Flow e use as referências visuais para manter consistência de avatar e marca.</li>
          <li>Renderize as cenas respeitando as durações de cada take.</li>
          <li>Acesse a aba <strong>Versões de Vídeo</strong> deste estúdio e importe o link do vídeo finalizado para aplicar ao anúncio.</li>
        </ol>
      </div>
    </div>
  );
};
